import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function HomePage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/bd9417ec1a0de276e3577f79b06ff8a1.jpg"
            alt="Consulting Excellence"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center text-white w-full">
          <h1 className="text-4xl md:text-5xl font-semibold mb-8">
            20+ Years of Consulting Excellence
          </h1>
          <p className="text-xl md:text-2xl mb-10 max-w-3xl mx-auto font-light">
            Helping businesses solve complex challenges, grow revenue, and improve performance across Toronto and beyond
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link 
              to="/contact" 
              className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
            >
              Book a Consultation
            </Link>
            <Link 
              to="/services" 
              className="bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white border-2 border-white px-8 py-4 rounded-lg text-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
            >
              Our Services
            </Link>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">What We Do</h2>
            <div className="w-24 h-1 bg-teal-500 mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our sole focus is unlocking better growth for our clients, increasing their long-term sales, value, and profit through strategic consulting excellence.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Business Strategy',
                description: 'Develop comprehensive strategies that drive sustainable growth and competitive advantage in your market.',
                icon: 'ri-line-chart-line'
              },
              {
                title: 'Market Analysis',
                description: 'Deep market insights and competitive intelligence to inform strategic decision-making.',
                icon: 'ri-bar-chart-box-line'
              },
              {
                title: 'Pricing Strategy',
                description: 'Optimize your pricing models to maximize revenue while maintaining market competitiveness.',
                icon: 'ri-price-tag-3-line'
              },
              {
                title: 'Growth Consulting',
                description: 'Identify and capitalize on growth opportunities through data-driven strategies.',
                icon: 'ri-rocket-line'
              },
              {
                title: 'Operational Improvement',
                description: 'Streamline operations and enhance efficiency to reduce costs and improve performance.',
                icon: 'ri-settings-3-line'
              },
              {
                title: 'Organizational Change',
                description: 'Guide your organization through transformation with proven change management methodologies.',
                icon: 'ri-team-line'
              }
            ].map((service, index) => (
              <div 
                key={index}
                className="bg-gray-50 p-8 rounded-xl hover:shadow-xl transition-all duration-300 group cursor-pointer"
              >
                <div className="w-16 h-16 bg-teal-500 rounded-lg flex items-center justify-center mb-6 group-hover:bg-teal-600 transition-colors">
                  <i className={`${service.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link 
              to="/services" 
              className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-semibold text-lg cursor-pointer"
            >
              View All Services
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">Industries We Serve</h2>
            <div className="w-24 h-1 bg-teal-500 mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Deep industry expertise across multiple sectors, delivering tailored solutions that drive measurable results.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              { name: 'Technology', icon: 'ri-computer-line' },
              { name: 'Finance', icon: 'ri-bank-line' },
              { name: 'Healthcare', icon: 'ri-heart-pulse-line' },
              { name: 'Manufacturing', icon: 'ri-factory-line' },
              { name: 'Professional Services', icon: 'ri-briefcase-line' }
            ].map((industry, index) => (
              <div 
                key={index}
                className="bg-white p-6 rounded-lg text-center hover:shadow-lg transition-all cursor-pointer group"
              >
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-teal-500 transition-colors">
                  <i className={`${industry.icon} text-3xl text-teal-600 group-hover:text-white transition-colors`}></i>
                </div>
                <h3 className="font-semibold text-gray-900">{industry.name}</h3>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link 
              to="/industries" 
              className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-semibold text-lg cursor-pointer"
            >
              Explore Industries
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Key Stats */}
      <section className="py-20 px-6 bg-gradient-to-br from-teal-600 to-teal-700 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-12 text-center">
            <div>
              <div className="text-5xl font-bold mb-2">20+</div>
              <div className="text-xl font-light">Years of Experience</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">500+</div>
              <div className="text-xl font-light">Successful Projects</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">98%</div>
              <div className="text-xl font-light">Client Satisfaction</div>
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies Preview */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">Success Stories</h2>
            <div className="w-24 h-1 bg-teal-500 mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Real results for real businesses. See how we've helped our clients achieve measurable growth and success.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                industry: 'Technology',
                title: 'Revenue Growth Strategy',
                result: '150% revenue increase',
                description: 'Helped a SaaS company optimize pricing and go-to-market strategy, resulting in significant revenue growth.'
              },
              {
                industry: 'Healthcare',
                title: 'Operational Excellence',
                result: '40% cost reduction',
                description: 'Streamlined operations for a healthcare provider, reducing costs while improving patient care quality.'
              },
              {
                industry: 'Manufacturing',
                title: 'Market Expansion',
                result: '5 new markets entered',
                description: 'Developed and executed market entry strategy for a manufacturing firm, successfully expanding into new territories.'
              }
            ].map((study, index) => (
              <div 
                key={index}
                className="bg-white border border-gray-200 rounded-xl p-8 hover:shadow-xl transition-all cursor-pointer"
              >
                <div className="text-teal-600 font-semibold mb-2">{study.industry}</div>
                <h3 className="text-2xl font-bold mb-3 text-gray-900">{study.title}</h3>
                <div className="text-3xl font-bold text-teal-600 mb-4">{study.result}</div>
                <p className="text-gray-600 leading-relaxed">{study.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link 
              to="/case-studies" 
              className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-semibold text-lg cursor-pointer"
            >
              View All Case Studies
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Transform Your Business?</h2>
          <p className="text-xl mb-8 text-gray-300">
            Let's discuss how our expertise can help you achieve your business goals. Schedule a consultation with our team today.
          </p>
          <Link 
            to="/contact" 
            className="inline-block bg-teal-500 hover:bg-teal-600 text-white px-10 py-4 rounded-lg text-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
          >
            Book Your Consultation
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
